import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import Legend from '../../../../../app/maps/components/Legend'
import { Map, MapContextProvider, useMapContext } from '../../../../../app/maps/components/Map'
import { MAP_SUBTYPE_DAY_PERF, MAP_TYPE_ETF_HOLDINGS_WIDGET } from '../../../../../app/maps/constants/constants'
import { SettingsScreener, SettingsScreenerDark } from '../../../../../app/maps/constants/settings'
import { MapSubtypeId } from '../../../../../app/maps/types'
import { onNodeClick } from '../../../../../app/maps/utils'
import { Button } from '../../../../components/button'
import { DefaultErrorBoundary } from '../../../../components/error-view'
import { useRefetchPerf } from '../../../portfolio/hooks/use-refetch-perf'
import { PortfolioGroup } from '../../../portfolio/types'
import { ETFHoldingsDialogType, ETFHoldingsStandardResponse } from '../types'
import { getStandardMapData } from '../utils'

interface IStandardMap {
  data: ETFHoldingsStandardResponse
  limitDataTo?: number
  handleDialogToggle?: (value: ETFHoldingsDialogType) => void
  type: string
}

export function StandardMap(props: IStandardMap) {
  return (
    <ErrorBoundary FallbackComponent={DefaultErrorBoundary}>
      <MapContextProvider>
        <StandardMapContent {...props} />
      </MapContextProvider>
    </ErrorBoundary>
  )
}

function StandardMapContent({ data, limitDataTo, handleDialogToggle, type }: IStandardMap) {
  const isPremium = FinvizSettings.hasUserPremium
  const { treemap } = useMapContext()

  const perfQuery = useRefetchPerf({
    enabled: true,
    subtype: MapSubtypeId.DayPerf,
    portfolioEntries: data.rowData as unknown as PortfolioGroup[],
  })

  const mapData = React.useMemo(
    () => getStandardMapData({ data, limitDataTo, perfData: perfQuery.data }),
    [data, limitDataTo, perfQuery.data]
  )

  return (
    <>
      <div className="flex grow px-2" data-testid={`etf-holdings-${type}`}>
        <Map
          className="flex-1 overflow-hidden"
          isFetchingPerf={false}
          isLoadingData={false}
          data={mapData.data}
          dataHash={perfQuery.data?.hash}
          type={MAP_TYPE_ETF_HOLDINGS_WIDGET}
          subtype={MAP_SUBTYPE_DAY_PERF}
          settings={FinvizSettings.hasDarkTheme ? SettingsScreenerDark : SettingsScreener}
          onNodeClick={(nodeProps) => {
            if (!nodeProps.node?.data?.data?.ticker) return
            onNodeClick(nodeProps)
          }}
          legend={false}
          zoom={false}
        />
      </div>

      <div className="flex min-h-9 items-center justify-between px-2 py-1.5">
        {limitDataTo && data.etfHoldingsTotal > limitDataTo ? (
          <Button
            theme="transparent"
            size="small"
            onClick={() => handleDialogToggle?.(ETFHoldingsDialogType.Map)}
            className="tab-link flex cursor-pointer items-center text-link hover:underline"
            leftContent={!isPremium ? 'lock' : undefined}
            data-testid={`etf-holdings-${type}-btn-show-all`}
          >
            Show All {data.etfHoldingsTotal} Holdings
          </Button>
        ) : (
          <div />
        )}
        {treemap && <Legend treemap={treemap} />}
      </div>
    </>
  )
}
